// ===== TIPS チュートリアルカード (2026-07-12) =====
// 稽古中に「TIPSカードを読む → その場で実践 → クリア → 次のカード」の流れを作る。
//   カードは各課題の"実践前"に挟み、閉じるまで戦闘をポーズする (game.ts が制御)。
//   枠 = 生成アート素材、中身 (タイトル/入力/本文/写真/ページ番号) は render.ts drawTipsCard が合成。
//
// ⚠️ 文言のルール (リサーチ準拠・2026-07-12 全面改訂):
//   1. **入力(ボタン)から書く** — Lies of P の TIPS が「L1：ガード」で始まるのと同じ。
//      どのボタンを押すのか書いていないチュートリアルは、チュートリアルとして失格。
//   2. **動詞から。答えから。前置きしない** (UXライティングの定説)。
//   3. **平易な言葉で、2〜3行** ("Show, don't tell" / 「マニュアルを丸ごと投げるな」)。
//   4. **詩を書かない。機構を書く。** 比喩や情緒は本文でなく世界観 (枠/書体/写真) が担う。
//   5. **嘘を書かない** — 下記は src 実コードで裏取り済 (hits.ts / player.ts / input.ts / render.ts HUD)。
//
// 【裏取り済みの機構】(hits.ts:167-206 / game.ts:587 / input.ts / render.ts drawPlayerHud)
//   - 入力: パリィ=Space/K・L1 / 回避=右クリック/J・○ / 攻撃=左クリック/L・R1 (2026-07-24 パッド表記正誤同期)
//   - パリィは1種類 (2026-10-10 ユーザー裁定・src/parry_one.ts): 押してから 120ms 以内に受けた弾きは全部同じ扱い。
//     相手の体幹が大きく溜まる。自分の体幹は少し溜まるが、溢れても踏みとどまる (パリィでは自分がスタンしない)。
//     120ms より早い押しは弾きにならない (押し続ければガード・離していれば当たる)。
//   - 崩しは2経路: 弾いた時 (hits.ts) は 相手の体幹が満ちるとその場で崩れる (台帳 combat.md 2026-10-09「1種類にする」)。
//     攻撃を当てた時 (combat.ts:234-236) は非ガードなら、相手posture>=max または vitalityEmpty で崩れる。
//   - 稽古の課題 lesson1 (goal: just_parry) はコード上は残っている。1種類ではどのパリィも同じ弾きの見た目 (白い火花・isJust) が出るので、
//     lesson0 と同じ「パリィを決める」を 2 回に増やした形になる (課題の構造は変えていない・文言だけ揃えた)。
//   - HUD: 左上のロウソク=プレイヤーHP(命灯) / その下の紫バー=プレイヤー体幹 / 画面下=相手HP+相手体幹
//   - ⚠️スタミナ: 機構としては在るが **HUDにバーが無い(2026-06-17 廃止)** → 「ゲージを見ろ」とは書けない

import { tutFocusMode } from './tut_focus'

export type TutorialCard = {
  id: string
  title: string
  // 押すボタン (キーバッジとして描画)。無い回 (画面説明) は省略。
  //   ⚠️ 操作は必ずバッジで見せる = 本文に素のテキストで「左クリック」と書かない (見た目が不統一になる)。
  //   複数の操作を使う回 (躱す→反撃 等) は 2 枚並べる。
  inputs?: string[]
  body: string[]        // 各行 = 短い因果 (画面で読める分量・2〜3行)
  photoKey: string      // 右の写真枠にはめる実機キャプチャ (public/ui/tips/<key>.png)
  // この課題 index の実践"前"に表示する。同 triggerLesson が複数あれば順に表示。
  //   lessons.length 以上 = 仕上げ (崩し→致命) の前。
  triggerLesson: number
}

// 課題クリア → 次のカードを出すまでの「間」(ms)。この間は戦闘が動き続け、成功の余韻
//   (パリィ閃光・ヒットストップ・ボスが構えに戻る) を見せる。0 だと勝った瞬間に紙が
//   飛び出して「バグかと思った」になる (2026-07-12 実機FB)。
export const TIPS_CARD_HOLD_MS = 900

// ボス別の TIPS カード表 (未登録ボス = カード無し = 従来通りの稽古)。
//   配布版の既定 (= ?tutFocus=3 と同じ) は亡霊のカードを1枚も出さない (下の every)。SHADE_TIPS_CARDS を出すのは旧稽古の DEV ?tutFocus=0 だけ。
//   比較版 (DEV ?tutFocus=1|2) の時は、課題の並びに合わせたカード表へ替える。
export function tipsCardsForBoss(bossId: string): TutorialCard[] {
  if (bossId !== 'nameless-shade') return []
  const m = tutFocusMode()
  // 3 (全部の課題を止めて教える) は亡霊のカードを1枚も出さない (画面の見かたも止めて教える・src/tut_focus.ts hud_self/hud_boss)。
  if (m === 'every') return []
  return m === 'exec' ? SHADE_FOCUS_EXEC_CARDS : m === 'all' ? SHADE_FOCUS_ALL_CARDS : SHADE_TIPS_CARDS
}

// いま出すべきカードの index (無ければ -1)。
//   cards は triggerLesson 昇順。cursor 番目のカードの triggerLesson に稽古が到達していれば出す。
//   → 「カードを読む → 閉じる (cursor++) → その課題を実践 → クリア (lesson++) → 次のカード」。
export function pendingTipsCardIndex(cards: TutorialCard[], lesson: number, cursor: number): number {
  if (cursor >= cards.length) return -1
  return cards[cursor]!.triggerLesson <= lesson ? cursor : -1
}

// 亡霊 (nameless-shade) の旧稽古 (DEV ?tutFocus=0) のカード。lessons = [0:parry / 1:just_parry / 2:guard /
//   3:counter突き / 4:counter掴み / 5:zone_evade波 / 6+:仕上げ(崩し→致命)]。
export const SHADE_TIPS_CARDS: TutorialCard[] = [
  {
    id: 'ui',
    title: '画面の見かた',
    body: [
      '左上のロウソクが、LUNAIREの体力。攻撃を受けると減る。',
      // 2026-07-26 訂正: 体幹バーは 2026-07-22 D1 でボスと同じ金系へ変更済み (render.ts drawPlayerHud)。
      //   カード内の写真は金に更新されていたのに、文だけ「紫」で取り残されていた (⑥の棚卸しで発見)。
      'その下の金色のゲージが、LUNAIREの体幹。溜まりきると体勢を崩される。',
      '画面下が、相手の体力と体幹。相手の体幹を溜めきるのが、倒す近道。',
    ],
    photoKey: 'tips_ui',
    triggerLesson: 0,
  },
  {
    id: 'parry',
    title: 'パリィ',
    inputs: ['{parry}＝ パリィ'],
    body: [
      '相手の攻撃に合わせて押す。ダメージを防ぎ、相手の体幹を溜める。',
      'ただしLUNAIREの体幹も溜まる。連打せず、攻撃を見てから合わせる。',
    ],
    photoKey: 'tips_parry',
    triggerLesson: 0,
  },
  {
    id: 'just_parry',
    title: 'パリィの合わせかた',
    inputs: ['当たる直前に {parry}'],
    body: [
      // 2026-10-10 パリィは1種類 (ユーザー裁定)。旧「ジャストパリィ」のカード。ジャスト／普通の区別を消し、
      //   受付の事実 (当たる直前に押す・早押しはパリィにならず、押し続ければガード) を書いた。自分の体幹が少し溜まる事実は 10-09 の訂正のまま
      //   (hits.ts perfectParrySelfPostureGain・tests/defeat_tips_facts.test.ts が実測)。
      '当たる直前に押す。白い火花が散れば成功。',
      'LUNAIREの体幹はわずかに、相手の体幹は大きく溜まる。',
      '早く押しすぎると、パリィにならない。押し続ければ、ガードになる。',
    ],
    photoKey: 'tips_just_parry',
    triggerLesson: 1,
  },
  {
    id: 'guard',
    title: 'ガード',
    inputs: ['{parry} を長押し＝ ガード'],
    body: [
      // 🔵 2026-09-04 裁定でガードは「HPを守り、体幹で払う」形になった (台帳 §パリィ長押しガード)。
      //   旧文「ダメージを大きく減らせる」はチップ 0.3 前提だったので、実態に合わせて書き直した。
      '長押し＝ガード。タップ＝パリィ。',
      'パリィできる攻撃なら、体力は減らない。そのぶん体幹が大きく溜まる。',
      'ガード中は動けず、攻撃できない。',
    ],
    // 写真が未着でも render.ts は空枠を保つため、カード表示と進行は壊れない。
    photoKey: 'tips_guard',
    triggerLesson: 2,
  },
  {
    id: 'evade',
    title: '回避カウンター',
    // 2操作を使う回 = バッジ2枚。本文に素のテキストでボタン名を書かない (見た目の統一)。
    inputs: ['{step}＝ 回避', '{attack}＝ 回避カウンター'],
    body: [
      '赤黒いオーラの攻撃は、パリィもガードもできない。回避する。',
      '赤黒いオーラの攻撃を回避した直後だけ、回避カウンターが刺さる。',
    ],
    photoKey: 'tips_evade',
    triggerLesson: 3,
  },
  {
    id: 'zone',
    title: '位置どり',
    inputs: ['{move}＝ 歩く'],
    body: [
      // 2026-10-09 訂正: 回避の無敵は床にも効く (台帳 combat.md 09-16) が、波は無敵 (0.1 秒・10-14 からは 0.2 秒) より長く残るので、
      //   その場で回避しても無敵が切れた所で当たる (10-14〜 は狭い帯ならまん中からの回避で出きれる・tests/defeat_tips_facts.test.ts [floor-stays])。旧文「回避でも抜けられない」は無敵が効かないように読めたので直した (tests/defeat_tips_facts.test.ts [floor-stays])。
      '床に広がる波は、パリィできない。回避の無敵より長く残る。',
      '早めに歩いて、波の外へ逃げる。',
    ],
    photoKey: 'tips_zone',
    triggerLesson: 5,
  },
  {
    id: 'execute',
    title: '崩して、致命の一撃',
    inputs: ['{attack}＝ 致命の一撃'],
    body: [
      // 2026-10-05 ユーザー裁定「色に任せて短く」(調査 §5 案2)。赤は「次のパリィ1回で崩れる」所から点く
      //   (boss_hud_view.ts breakReadyRatio)。攻撃で崩れるのは満タンの時だけなので、赤の文には入れない。
      //   旧文「体幹が満タンになるか、体力が尽きると崩れる」は亡霊では誤りだった (docs/research/2026-10-05-shade-practice-stagger.md)。
      '体幹のゲージが赤く光ったら、パリィで崩れる。',
      '崩れた相手へ攻撃すると、致命の一撃。これが倒し方。',
    ],
    photoKey: 'tips_execute',
    triggerLesson: 6,
  },
]

// 比較版 (DEV ?tutFocus=1) のカード表: 課題の並び = 0 パリィ / 1 ガード / 2 崩されて弾く (止めて教えるのでカード無し) / 3 突き / 4 掴み / 5 波。
//   旧「パリィの合わせかた」はパリィのカードの続きに出す (10-11 答え exec-tut-oldcard = A)。ガードは課題1の前へ。他は番号が同じ。
export const SHADE_FOCUS_EXEC_CARDS: TutorialCard[] = SHADE_TIPS_CARDS.map((c) =>
  c.id === 'just_parry' ? { ...c, triggerLesson: 0 } : c.id === 'guard' ? { ...c, triggerLesson: 1 } : c)
// 比較版 (DEV ?tutFocus=2): パリィも止めて教えるので、パリィの2枚 (パリィ・合わせかた) を出さない。
export const SHADE_FOCUS_ALL_CARDS: TutorialCard[] = SHADE_FOCUS_EXEC_CARDS.filter((c) => c.id !== 'parry' && c.id !== 'just_parry')
